import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Row,
  Select,
  Typography,
} from 'antd';
import type { FormProps } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getAllSim900WaitingActivation } from 'src/api/activeSim';
import CGridView from 'src/components/CGridView';
import TableContent from 'src/components/CTable';
import useWindowSize from 'src/hooks/useWindowSize';
import { OpenNewTab } from 'src/shared/common/openNewTab';
import { Sim900WaitingActivationStatus, SimType } from 'src/shared/enum/sim.enum';
import {
  Sim900WaitingActivationItem,
  Sim900WaitingActivationSearchParams,
} from 'src/shared/interface/sim900.interface';
import {
  exportExcel,
  formatDate,
  formatDateTime,
  formatPhoneNumberV2,
  nonAccentVietnamese,
  parseQueryString,
} from 'src/utils';
import { simTypeToText } from 'src/utils/sim.enum';
import Sim900StatusTag, { getSim900StatusTagConfig } from '../components/Sim900StatusTag';

const MAX_ACTIVATION_EXPIRED_RANGE_DAYS = 90;
const MAX_CREATED_RANGE_DAYS = 90;

type Sim900ListFormValues = {
  phoneNumber?: string;
  serial?: string;
  status?: Sim900WaitingActivationStatus | string;
  simType?: string;
  activationExpiredRange?: [Dayjs, Dayjs];
  createdRange?: [Dayjs, Dayjs];
};

const statusOptions = [
  { label: 'Tất cả', value: '' },
  ...Object.values(Sim900WaitingActivationStatus)
    .filter((status): status is Sim900WaitingActivationStatus => typeof status === 'number')
    .map((status) => ({
      label: getSim900StatusTagConfig(status).label,
      value: status.toString(),
    })),
];

export default function Sim900WaitingActivationListPage() {
  const [form] = Form.useForm<Sim900ListFormValues>();
  const location = useLocation();
  const size = useWindowSize();
  const [_, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PaginationResponse<Sim900WaitingActivationItem>>();
  const [activationExpiredDates, setActivationExpiredDates] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [createdDates, setCreatedDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const disabledActivationExpiredDate: RangePickerProps['disabledDate'] = (current) => {
    if (!current || !activationExpiredDates) {
      return false;
    }

    const [startDate, endDate] = activationExpiredDates;
    const selectedDate = startDate ?? endDate;

    if (!selectedDate) {
      return false;
    }

    return Math.abs(current.diff(selectedDate, 'day')) > MAX_ACTIVATION_EXPIRED_RANGE_DAYS;
  };

  const disabledCreatedDate: RangePickerProps['disabledDate'] = (current) => {
    if (!current) {
      return false;
    }

    if (current.isAfter(dayjs().endOf('day'))) {
      return true;
    }

    if (!createdDates) {
      return false;
    }

    const [startDate, endDate] = createdDates;
    const selectedDate = startDate ?? endDate;

    if (!selectedDate) {
      return false;
    }

    return Math.abs(current.diff(selectedDate, 'day')) > MAX_CREATED_RANGE_DAYS;
  };

  const validateCreatedRange = (value?: [Dayjs | null, Dayjs | null]) => {
    if (!value) {
      return Promise.resolve();
    }

    const [startDate, endDate] = value;

    if (!startDate || !endDate) {
      return Promise.resolve();
    }

    if (!startDate.isValid() || !endDate.isValid()) {
      return Promise.reject(new Error('Ngày tạo không hợp lệ'));
    }

    if (startDate.isAfter(dayjs().endOf('day')) || endDate.isAfter(dayjs().endOf('day'))) {
      return Promise.reject(new Error('Ngày tạo không được lớn hơn ngày hiện tại'));
    }

    if (Math.abs(endDate.diff(startDate, 'day')) > MAX_CREATED_RANGE_DAYS) {
      return Promise.reject(new Error('Khoảng ngày tạo tối đa là 90 ngày'));
    }

    return Promise.resolve();
  };

  const buildSearchParams = (
    values: Sim900ListFormValues,
    pageIndex = 1,
    pageSize = 10,
  ): Sim900WaitingActivationSearchParams => ({
    pageIndex,
    pageSize,
    phoneNumber: values.phoneNumber,
    serial: values.serial,
    status: values.status && values.status !== '' ? values.status : undefined,
    simType: values.simType && values.simType !== '' ? values.simType : undefined,
    activationExpiredFrom: values.activationExpiredRange?.[0]?.format('YYYY-MM-DD'),
    activationExpiredTo: values.activationExpiredRange?.[1]?.format('YYYY-MM-DD'),
    DateFrom: values.createdRange?.[0]?.format('YYYY-MM-DD'),
    DateTo: values.createdRange?.[1]?.format('YYYY-MM-DD'),
  });

  const fetchData = async (params: Sim900WaitingActivationSearchParams) => {
    const response = await getAllSim900WaitingActivation(params);

    setData(response);
  };

  useEffect(() => {
    const params = parseQueryString(location.search) as Sim900WaitingActivationSearchParams;
    fetchData({
      pageIndex: Number(params.pageIndex ?? 1),
      pageSize: Number(params.pageSize ?? 10),
      ...params,
    });
    if (!location.search) {
      form.resetFields();
    } else {
      form.setFieldsValue(params);
    }
  }, [location.search]);

  const handleSearch = (values: Sim900ListFormValues) => {
    const currentParams = parseQueryString(location.search);
    const nextParams = buildSearchParams(values, 1, Number(currentParams.pageSize ?? 10));

    setSearchParams(queryString.stringify(nextParams));
  };

  const handleClearFilter = () => {
    form.resetFields();
    setSearchParams({ pageIndex: '1', pageSize: '10' });
  };

  const handleOpenDetail = (record: Sim900WaitingActivationItem) => {
    OpenNewTab(
      `active-sim/sim900/detail?id=${record.id}`,
      `Chi tiết TB chờ kích hoạt ${record.phoneNumber}`,
    );
  };

  const handleExport = () => {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `Danh_sach_TB_cho_kich_hoat_${dayjs().format('DDMMYYYY_HHmmss')}.xlsx`;

    const params = parseQueryString(location.search);
    const query = queryString.stringify(params);
    const url = `${process.env.REACT_APP_API_URL}/api/active-sim/sim900/export-excel${
      query ? `?${query}` : ''
    }`;

    exportExcel(url)
      .then((res) => {
        message.success('Xuất file excel thành công');
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error: { status?: number }) => {
        if (error.status === 403) {
          message.error('Tài khoản của bạn không có quyền truy cập vào chức năng này');
        }
      });
  };

  const columns: IListColumn[] = useMemo(
    () => [
      { name: 'STT', fieldName: 'index', align: 'center', isHideSort: true, width: 60 },
      {
        name: 'SĐT',
        fieldName: 'phoneNumber',
        align: 'center',
        isHideSort: true,
        width: 140,
        renderSpecialCard: (record: Sim900WaitingActivationItem) => (
          <Typography.Text
            className='underline whitespace-nowrap cursor-pointer text-blue-400 font-semibold'
            onClick={() => handleOpenDetail(record)}
          >
            {formatPhoneNumberV2(record.phoneNumber ?? '')}
          </Typography.Text>
        ),
      },
      {
        name: 'Serial',
        fieldName: 'serial',
        align: 'center',
        width: 180,
      },
      {
        name: 'Loại SIM',
        fieldName: 'simType',
        align: 'center',
        width: 130,
        renderSpecialCard: (record: Sim900WaitingActivationItem) => (
          <span className='whitespace-nowrap'>{simTypeToText(Number(record.simType))}</span>
        ),
      },
      {
        name: 'Gói cước ban đầu',
        fieldName: 'package',
        align: 'center',
        width: 160,
      },
      {
        name: 'Trạng thái',
        fieldName: 'statusString',
        align: 'center',
        width: 150,
        renderSpecialCard: (record: Sim900WaitingActivationItem) => (
          <Sim900StatusTag showWarning={false} status={record.status} />
        ),
      },
      {
        name: 'Hết hạn kích hoạt',
        fieldName: 'activationExpiredAt',
        align: 'center',
        width: 130,
        renderSpecialCard: (record: Sim900WaitingActivationItem) => (
          <span className='whitespace-nowrap'>{formatDate(record.activationExpiredAt ?? '')}</span>
        ),
      },
      {
        name: 'Ngày tạo',
        fieldName: 'createdAt',
        align: 'center',
        width: 150,
        renderSpecialCard: (record: Sim900WaitingActivationItem) => (
          <span className='whitespace-nowrap'>{formatDateTime(record.createdAt ?? '')}</span>
        ),
      },
      {
        name: 'Mã DN',
        fieldName: 'enterpriseCode',
        align: 'center',
        width: 100,
      },
      {
        name: 'Tên hồ sơ DN',
        fieldName: 'createdEnterprise',
        align: 'center',
        width: 150,
      },
      {
        name: 'Thao tác',
        fieldName: 'action',
        align: 'center',
        isHideSort: true,
        width: 100,
        fixed: 'right',
        renderSpecialCard: (record: Sim900WaitingActivationItem) => (
          <Typography.Text
            className='underline whitespace-nowrap cursor-pointer text-blue-400 font-semibold'
            onClick={() => handleOpenDetail(record)}
          >
            Chi tiết
          </Typography.Text>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Form
        form={form}
        layout={size.width < 768 ? 'vertical' : 'inline'}
        initialValues={{ status: '', simType: '' }}
        className='gap-2'
        onFinish={handleSearch}
      >
        <Form.Item name='phoneNumber' label='Số điện thoại'>
          <Input maxLength={15} placeholder='Nhập SĐT' className='min-w-[180px]' />
        </Form.Item>
        <Form.Item name='serial' label='Serial SIM'>
          <Input maxLength={50} placeholder='Nhập Serial' className='min-w-[180px]' />
        </Form.Item>
        <Form.Item name='status' label='Trạng thái'>
          <Select options={statusOptions} className='min-w-[160px]' />
        </Form.Item>
        <Form.Item name='activationExpiredRange' label='Hết hạn kích hoạt'>
          <DatePicker.RangePicker
            placeholder={['Từ', 'Đến']}
            format='DD/MM/YYYY'
            disabledDate={disabledActivationExpiredDate}
            onCalendarChange={(dates) => setActivationExpiredDates(dates)}
            onOpenChange={(open) => {
              if (!open) {
                setActivationExpiredDates(null);
              }
            }}
          />
        </Form.Item>
        <Form.Item name='simType' label='Loại SIM'>
          <Select
            showSearch
            className='min-w-[150px]'
            filterOption={(input, option) => {
              return nonAccentVietnamese(
                (simTypeToText(option!.value) as unknown as string).toString()?.toLowerCase(),
              ).includes(nonAccentVietnamese(input?.toLowerCase()?.trim()));
            }}
            options={[
              {
                value: '',
                label: 'Tất cả',
              },
              ...Object.values(SimType)
                .filter((type): type is SimType => typeof type === 'number')
                .map((type) => ({
                  label: simTypeToText(type),
                  value: type.toString(),
                })),
            ]}
          />
        </Form.Item>
        <Form.Item
          name='createdRange'
          label='Ngày tạo'
          rules={[
            {
              validator: (_, value?: [Dayjs | null, Dayjs | null]) => validateCreatedRange(value),
            },
          ]}
        >
          <DatePicker.RangePicker
            placeholder={['Từ', 'Đến']}
            format='DD/MM/YYYY'
            disabledDate={disabledCreatedDate}
            onCalendarChange={(dates) => setCreatedDates(dates)}
            onOpenChange={(open) => {
              if (!open) {
                setCreatedDates(null);
              }
            }}
          />
        </Form.Item>
        <div className='row-button justify-end flex-wrap mt-3'>
          <Button onClick={handleClearFilter} className='m-1'>
            Xóa tìm kiếm
          </Button>
          <Button type='primary' htmlType='submit' className='m-1'>
            Tìm kiếm
          </Button>
          <Button onClick={handleExport} className='m-1'>
            Export
          </Button>
        </div>
      </Form>
      <div className='mt-4'>
        {size.width < 1024 ? (
          <CGridView
            length={data?.results?.length ?? 0}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            title={() => <div className='text-xl font-bold my-3'>Danh sách TB chờ kích hoạt</div>}
          >
            {data?.results?.length ? (
              data.results.map((item) => (
                <div key={`data-row-${item.id}`}>
                  <Divider className='border-t-black' />
                  <Row className='mb-6 items-center' gutter={[4, 16]}>
                    {columns.map((column) =>
                      column.fieldName !== 'index' ? (
                        <Col key={`${item.id}-${column.fieldName}`} span={24}>
                          <div className='grid grid-cols-12 gap-2'>
                            <div className='col-span-5 font-bold label'>{column.name}</div>
                            <div className='col-span-7 break-words text-right'>
                              {column.renderSpecialCard
                                ? column.renderSpecialCard(item, null, null, null)
                                : item[column.fieldName as keyof Sim900WaitingActivationItem]}
                            </div>
                          </div>
                        </Col>
                      ) : null,
                    )}
                  </Row>
                </div>
              ))
            ) : (
              <p>Không có dữ liệu</p>
            )}
          </CGridView>
        ) : (
          <TableContent
            bordered
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Không tìm thấy kết quả' }}
            dataSource={data?.results ?? []}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            onPageChange={(pageNumber, pageSize) => {
              const params = parseQueryString(location.search);
              setSearchParams({
                ...params,
                pageIndex: pageNumber.toString(),
                pageSize: pageSize.toString(),
              });
            }}
            onSort={(sort: ISortColumn) => {
              const params = parseQueryString(location.search);
              setSearchParams({
                ...params,
                pageIndex: params.pageIndex ?? '1',
                pageSize: params.pageSize ?? '10',
                sortField: sort.fieldName,
                asc: (sort.orderType === 'asc').toString(),
              });
            }}
          />
        )}
      </div>
    </>
  );
}
